/**
 * The public page for testing the Android app through Google Play's closed test (owner request 2026-10-25):
 * the tester signs up with the Google account email on their phone, then sees one step at a time and ticks
 * it as they do it.
 * Their phone keeps a token that finds their own row; nothing else of theirs is shown to anyone but the
 * administration.
 */
import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Copy, ExternalLink, Hourglass, Sparkles } from "lucide-react";
import {
  ANDROID_TEST_DAYS,
  ANDROID_TEST_REMIND_EVERY,
  TESTER_TOKEN_HEADER,
  type AndroidTesterMeResponse,
  type AndroidTesterStep,
  type PublicVolunteerResponse,
  type RegisterAndroidTesterResponse,
} from "@mubin/contracts";
import { ErrorNote, Field, useSubmit } from "../components/ui";
import { api, ApiError, errorText } from "../lib/api";
import { PhoneField } from "../components/phone-field";

const TOKEN_KEY = "mubin.androidTester";

const saveToken = (token: string) => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // Private browsing: the page still works for this visit.
  }
};
/** The personal link from a WhatsApp message (#t=…) wins over this browser's own token, and is kept. */
const readToken = () => {
  const fromLink = new URLSearchParams(window.location.hash.slice(1)).get("t");
  if (fromLink) {
    saveToken(fromLink);
    history.replaceState(null, "", window.location.pathname);
    return fromLink;
  }
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};
const PLAY_APP = "https://play.google.com/store/apps/details?id=app.mubin";

export function AndroidTestPage() {
  const qc = useQueryClient();
  const [token, setToken] = useState<string | null>(readToken);
  const org = useQuery({
    queryKey: ["public-volunteer"],
    queryFn: () => api<PublicVolunteerResponse>("/v1/public/volunteer"),
  });
  const me = useQuery({
    queryKey: ["android-tester", token],
    enabled: !!token,
    retry: false,
    queryFn: () =>
      api<AndroidTesterMeResponse>("/v1/public/android-testers/me", {
        headers: { [TESTER_TOKEN_HEADER]: token! },
      }),
  });
  const signUp = useSubmit();
  const step = useSubmit();
  const [form, setForm] = useState({ name: "", email: "", phone: "", website: "" });
  const [copied, setCopied] = useState<string | null>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!org.data) return;
    void signUp.run(
      async () => {
        const r = await api<RegisterAndroidTesterResponse>("/v1/public/android-testers", {
          method: "POST",
          json: {
            organizationId: org.data.organizationId,
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            website: form.website || undefined,
          },
        });
        saveToken(r.token);
        setToken(r.token);
      },
      (err) =>
        err instanceof ApiError && err.status === 429
          ? "محاولات كثيرة من هذا الجهاز، حاول لاحقًا."
          : errorText(err),
    );
  }

  function opened() {
    void step.run(async () => {
      await api("/v1/public/android-testers/me/opened", {
        method: "POST",
        headers: { [TESTER_TOKEN_HEADER]: token! },
      });
      await qc.invalidateQueries({ queryKey: ["android-tester", token] });
    }, errorText);
  }

  function tick(s: AndroidTesterStep) {
    void step.run(async () => {
      await api("/v1/public/android-testers/me/steps", {
        method: "POST",
        headers: { [TESTER_TOKEN_HEADER]: token! },
        json: { step: s },
      });
      await qc.invalidateQueries({ queryKey: ["android-tester", token] });
    }, errorText);
  }

  const copy = (text: string, what: string) =>
    void navigator.clipboard?.writeText(text).then(() => setCopied(what));

  const t = me.data;
  const signedUp = !!token && !me.isError;
  const done = { joined: t?.joinedAt, installed: t?.installedAt, tried: t?.triedAt };
  const current = (["joined", "installed", "tried"] as const).find((k) => !done[k]) ?? null;
  const index = current ? ["joined", "installed", "tried"].indexOf(current) : 3;
  const demo = t?.demoPhone
    ? `رقم الجوال: ${t.demoPhone}${t.demoPassword ? `\nكلمة المرور: ${t.demoPassword}` : ""}`
    : null;

  /** One step at a time (owner request 2026-10-25): short numbered instructions, one action, then «خلصت». */
  function stepCard() {
    if (!t) return null;
    if (current === "joined" && (!t.addedAt || !t.joinUrl))
      return (
        <div className="tester-wait">
          <Hourglass size={34} strokeWidth={1.5} />
          <h2>تم تسجيلك يا {t.name}</h2>
          <p>
            {t.addedAt ? "رابط الانضمام يجهز قريبًا." : "الحين دورنا: نضيف إيميلك في Google Play."}
            <br />
            بتوصلك رسالة واتساب أول ما يجهز، <b>ما عليك شي الحين</b>.
          </p>
        </div>
      );
    if (current === "joined")
      return (
        <>
          <h2>انضم للتجربة</h2>
          <Instructions
            items={["اضغط «افتح رابط الانضمام»", "اضغط «أصبح مختبِرًا»", "ارجع هنا واضغط «خلصت»"]}
          />
          <a className="btn btn-primary tester-action" href={t.joinUrl!} target="_blank" rel="noreferrer">
            <ExternalLink size={18} strokeWidth={1.75} /> افتح رابط الانضمام
          </a>
        </>
      );
    if (current === "installed")
      return (
        <>
          <h2>ثبّت التطبيق</h2>
          <Instructions
            items={[
              "إذا عندك نسخة مُبين قديمة احذفها أول",
              "اضغط «افتح Google Play» ثم «تثبيت»",
              "ارجع هنا واضغط «خلصت»",
            ]}
          />
          <a className="btn btn-primary tester-action" href={PLAY_APP} target="_blank" rel="noreferrer">
            <ExternalLink size={18} strokeWidth={1.75} /> افتح Google Play
          </a>
        </>
      );
    if (current === "tried")
      return (
        <>
          <h2>ادخل وجرّب التطبيق</h2>
          {demo ? (
            <>
              <p>افتح مُبين، واختر «كلمة المرور»، واكتب:</p>
              <dl className="tester-demo">
                <dt>رقم الجوال</dt>
                <dd className="ltr">{t.demoPhone}</dd>
                {t.demoPassword && (
                  <>
                    <dt>كلمة المرور</dt>
                    <dd className="ltr">{t.demoPassword}</dd>
                  </>
                )}
              </dl>
              <button
                type="button"
                className="btn btn-primary tester-action"
                onClick={() => copy(demo, "demo")}
              >
                <Copy size={18} strokeWidth={1.75} /> {copied === "demo" ? "نُسخت البيانات" : "نسخ البيانات"}
              </button>
              <p className="muted small">تجوّل في الورد والمصحف والسجل، ولا تحذف التطبيق قبل 14 يومًا 🤍</p>
            </>
          ) : (
            <p className="notice small">بيانات الحساب التجريبي تظهر هنا قريبًا.</p>
          )}
        </>
      );
    const day = t.testDay ?? 1;
    if (day > ANDROID_TEST_DAYS)
      return (
        <div className="tester-wait">
          <CheckCircle2 size={38} strokeWidth={1.5} />
          <h2>جزاك الله خيرًا يا {t.name}</h2>
          <p>
            انتهت أيام التجربة. <b>شكرًا لك</b> 🤍
          </p>
        </div>
      );
    return (
      <div className="tester-wait">
        {t.openedToday ? (
          <>
            <CheckCircle2 size={38} strokeWidth={1.5} />
            <h2>شكرًا، وصلنا تأكيدك ✓</h2>
            <p>
              كل {ANDROID_TEST_REMIND_EVERY} أيام نرسل لك تذكيرًا على الواتساب. <b>لا تحذف التطبيق</b> قبل
              نهاية الأيام.
            </p>
          </>
        ) : (
          <>
            <h2>التطبيق باقي على جوالك؟</h2>
            <p>
              افتح تطبيق مُبين ولو دقيقة، وبعدها أكّد هنا. كل {ANDROID_TEST_REMIND_EVERY} أيام نذكّرك على
              الواتساب.
            </p>
            <button
              type="button"
              className="btn btn-primary tester-action"
              disabled={step.busy}
              onClick={opened}
            >
              <Check size={18} strokeWidth={2} /> {step.busy ? "لحظة…" : "فتحت التطبيق ✓"}
            </button>
          </>
        )}
      </div>
    );
  }
  const canTick = current && (current !== "joined" || (!!t?.addedAt && !!t?.joinUrl));

  return (
    <main className="vol">
      <header className="vol-hero">
        <div className="vol-logos">
          <img
            src="/org/horizontal-white.png"
            alt={org.data?.organizationName ?? "الجمعية"}
            className="vol-org"
          />
          <span className="vol-sep" aria-hidden="true" />
          <img src="/logo-ivory.png" alt="مُبين" className="vol-mubin" />
        </div>
        <p className="vol-kicker">
          <Sparkles size={16} strokeWidth={1.75} /> فريق تجربة التطبيق
        </p>
        <h1>ساعدنا ننشر مُبين في Google Play</h1>
        <p className="vol-lead">3 خطوات بسيطة من جوالك الأندرويد، ونرسل لك كل خطوة على الواتساب.</p>
      </header>

      <div className="vol-body">
        {!signedUp ? (
          <section className="vol-card" id="vol-form">
            <h2>سجّل اسمك وإيميل Google</h2>
            <form className="form" onSubmit={submit}>
              <ErrorNote error={signUp.error} />
              <Field id="t-name" label="الاسم">
                <input
                  id="t-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </Field>
              <Field
                id="t-email"
                label="إيميل Gmail"
                hint="نفس الإيميل المسجّل في Google Play على جوالك الأندرويد"
              >
                <input
                  id="t-email"
                  type="email"
                  dir="ltr"
                  autoComplete="email"
                  placeholder="name@gmail.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </Field>
              <Field id="t-phone" label="رقم الواتساب" hint="تصلك عليه رسالة بكل خطوة">
                <PhoneField
                  id="t-phone"
                  value={form.phone}
                  onChange={(phone) => setForm({ ...form, phone })}
                  required
                />
              </Field>
              <div className="honeypot" aria-hidden="true">
                <label htmlFor="t-website">الموقع</label>
                <input
                  id="t-website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </div>
              <button
                className="btn btn-primary"
                disabled={
                  signUp.busy ||
                  !org.data ||
                  form.name.trim().length < 2 ||
                  form.phone.replace(/\D/g, "").length < 9 ||
                  !/^\S+@\S+\.\S+$/.test(form.email.trim())
                }
              >
                {signUp.busy ? "لحظة…" : "سجّلني"}
              </button>
            </form>
          </section>
        ) : (
          <section className="vol-card tester-card" aria-live="polite">
            {current && (
              <>
                <div className="tester-progress" aria-hidden="true">
                  {[0, 1, 2].map((i) => (
                    <i key={i} className={i < index ? "on" : i === index ? "cur" : ""} />
                  ))}
                </div>
                <p className="tester-count">الخطوة {index + 1} من 3</p>
              </>
            )}
            <ErrorNote error={step.error} />
            {stepCard()}
            {canTick && (
              <button
                type="button"
                className="btn btn-secondary tester-action tester-done"
                disabled={step.busy}
                onClick={() => tick(current)}
              >
                <Check size={18} strokeWidth={2} /> {step.busy ? "لحظة…" : "خلصت"}
              </button>
            )}
          </section>
        )}
      </div>
    </main>
  );
}

function Instructions({ items }: { items: string[] }) {
  return (
    <ol className="tester-list">
      {items.map((x) => (
        <li key={x}>{x}</li>
      ))}
    </ol>
  );
}
